import { useRoom, usePeerIds, useLocalPeer, useLocalAudio } from '@huddle01/react/hooks';
import { useEffect, useState, useCallback } from 'react';
import { useGameServer } from '../hooks/useGameServer';
import { useProximityAudio } from '../hooks/useProximityAudio';
import { PeerAudioList } from './PeerAudio';
import { MicControls } from './MicControls';
import { StatusDisplay } from './StatusDisplay';
import type { RoomConfig } from '../types';

interface VoiceRoomProps {
  config: RoomConfig;
}

/**
 * Main voice room component that orchestrates:
 * - Huddle01 room connection
 * - Game server WebSocket for positions
 * - Proximity-based audio volume
 */
export function VoiceRoom({ config }: VoiceRoomProps) {
  const [roomStatus, setRoomStatus] = useState<string>('idle');
  const [joinError, setJoinError] = useState<string | null>(null);
  const [peerAudioStates2, setPeerAudioStates2] = useState<Record<string, { state: string; isAudioOn: boolean }>>({});
  const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState<string>('');
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [outputSelectionSupported, setOutputSelectionSupported] = useState(false);

  // Huddle01 hooks
  const { joinRoom, leaveRoom, state: roomState } = useRoom({
    onJoin: () => {
      console.log('[ProxChat] Joined voice room');
      setRoomStatus('connected');
      setJoinError(null);
    },
    onLeave: () => {
      console.log('[ProxChat] Left voice room');
      setRoomStatus('disconnected');
    },
    onFailed: (reason) => {
      console.error('[ProxChat] Failed to join room:', reason);
      setJoinError(`Failed to join: ${reason.message}`);
      setRoomStatus('error');
    },
  });

  const { peerIds } = usePeerIds();
  const { peerId: myPeerId } = useLocalPeer();
  const { enableAudio, isAudioOn: isLocalAudioOn } = useLocalAudio();

  // Game server WebSocket for positions
  // For test mode, pass huddlePeerId so it can send identify_test with the correct peerId
  const { 
    status: wsStatus, 
    positions, 
    error: wsError,
  } = useGameServer({
    wsUrl: config.wsUrl,
    peerId: config.peerId,
    testMode: config.testMode,

    displayName: 'TestUser',
    huddlePeerId: myPeerId || undefined,
  });

  // Calculate proximity volumes - use Huddle01 peerId for test mode, config.peerId for normal
  const effectivePeerId = config.testMode && myPeerId ? myPeerId : config.peerId;
  const { peerAudioStates, myPosition } = useProximityAudio({
    myPeerId: effectivePeerId,
    positions,
  });

  // Join room on mount
  useEffect(() => {
    let cancelled = false;
    
    const doJoin = async () => {
      if (config.roomId && config.token && roomState === 'idle') {
        setRoomStatus('connecting');
        
        // Small delay to ensure HuddleClient is fully initialized
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (cancelled) return;
        
        try {
          console.log('[ProxChat] Joining room:', config.roomId);
          await joinRoom({
            roomId: config.roomId,
            token: config.token,
          });
        } catch (err) {
          console.error('[ProxChat] Join room error:', err);
          if (!cancelled) {
            setJoinError(`Failed to join: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setRoomStatus('error');
          }
        }
      }
    };
    
    doJoin();

    // Cleanup on unmount
    return () => {
      cancelled = true;
      if (roomState === 'connected') {
        leaveRoom();
      }
    };
  }, [config.roomId, config.token, roomState, joinRoom, leaveRoom]);

  // Auto-enable audio after joining room
  useEffect(() => {
    const autoEnableAudio = async () => {
      if (roomState === 'connected' && !isLocalAudioOn) {
        console.log('[ProxChat] Auto-enabling microphone after room join...');
        try {
          await enableAudio();
          console.log('[ProxChat] Microphone auto-enabled successfully');
        } catch (err) {
          console.error('[ProxChat] Failed to auto-enable microphone:', err);
          // Don't show error - user can manually enable
        }
      }
    };
    
    // Small delay to ensure room is fully connected
    const timer = setTimeout(autoEnableAudio, 1000);
    return () => clearTimeout(timer);
  }, [roomState, isLocalAudioOn, enableAudio]);

  // Log peer changes and position map keys to debug mismatches
  useEffect(() => {
    console.log('[ProxChat] Peer IDs changed:', peerIds);
    if (Object.keys(positions).length > 0) {
      console.log('[ProxChat] Position Map Keys:', Object.keys(positions));
      
      // Check for mismatches
      peerIds.forEach(pid => {
        if (!positions[pid]) {
          console.warn(`[ProxChat] Mismatch: Peer ${pid} is in Huddle room but has NO POSITION data.`);
        }
      });
    }
  }, [peerIds, positions]);

  // Audio output device selection (best-effort - only supported in some browsers)
  useEffect(() => {
    const supported = typeof (HTMLMediaElement.prototype as unknown as { setSinkId?: unknown }).setSinkId === 'function';
    setOutputSelectionSupported(supported);

    const refreshOutputDevices = async () => {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devices = await navigator.mediaDevices.enumerateDevices();
      setAudioOutputDevices(devices.filter(d => d.kind === 'audiooutput'));
    };

    // Try to refresh on mount; labels may be blank until mic permission is granted.
    refreshOutputDevices();

    const handler = () => refreshOutputDevices();
    navigator.mediaDevices?.addEventListener?.('devicechange', handler);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', handler);
  }, []);

  // Callback for peer audio state changes
  const handlePeerStateChange = useCallback((peerId: string, state: string, isAudioOn: boolean) => {
    setPeerAudioStates2(prev => ({
      ...prev,
      [peerId]: { state, isAudioOn }
    }));
  }, []);

  // Handle mic mute state changes - notify game server
  const handleMicToggle = (isMuted: boolean) => {
    // The game server will pick this up from the next position broadcast
    // since the VoiceSession tracks muted state
    console.log(`[ProxChat] Mic ${isMuted ? 'muted' : 'unmuted'}`);
  };

  const combinedError = joinError || wsError;

  return (
    <div className="voice-room">
      <header className="voice-room-header">
        <h1>ProxChat</h1>
        <p className="subtitle">Proximity Voice Chat</p>
      </header>

      <main className="voice-room-main">
        {/* Status Display */}
        <StatusDisplay
          wsStatus={wsStatus}
          roomStatus={roomStatus}
          peerAudioStates={peerAudioStates}
          myPosition={myPosition}
          error={combinedError}
        />

        {/* Mic Controls */}
        <div className="controls-section">
          <MicControls onMicToggle={handleMicToggle} />

          {/* Output device selection */}
          <div className="output-controls">
            <label className="output-label">Output Device</label>
            <select
              className="output-select"
              value={selectedOutputDeviceId}
              onChange={(e) => setSelectedOutputDeviceId(e.target.value)}
              disabled={!outputSelectionSupported || audioOutputDevices.length === 0}
            >
              <option value="">System Default</option>
              {audioOutputDevices.map(d => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || `Speaker ${d.deviceId.slice(0, 8)}`}
                </option>
              ))}
            </select>
            {!outputSelectionSupported && (
              <div className="output-hint">Browser doesn’t support output selection</div>
            )}
          </div>
        </div>

        {/* Hidden audio elements for each peer */}
        <PeerAudioList 
          peerAudioStates={peerAudioStates} 
          allPeerIds={peerIds}
          onPeerStateChange={handlePeerStateChange}
          selectedOutputDeviceId={selectedOutputDeviceId || undefined}
        />

        {/* Debug info - always show in this version for debugging */}
        <div className="debug-info">
          <details open>
            <summary>Debug Info</summary>
            <pre>
Room State: {roomState}
My Peer ID: {myPeerId || config.peerId}
Local Audio On: {isLocalAudioOn ? 'YES' : 'NO'}
Connected Peers: {peerIds.length}
Peer IDs: {peerIds.join(', ') || 'none'}
Positions Tracked: {Object.keys(positions).length}

Remote Peer Audio States:
{Object.entries(peerAudioStates2).map(([id, s]) => 
  `  ${id.slice(0,8)}: state=${s.state}, audioOn=${s.isAudioOn}`
).join('\n') || '  (none yet)'}
            </pre>
          </details>
        </div>
      </main>

      <footer className="voice-room-footer">
        <button 
          className="leave-button"
          onClick={() => {
            leaveRoom();
            window.close();
          }}
        >
          Leave Voice Chat
        </button>
      </footer>
    </div>
  );
}
